import { parseJSON, setSeconds } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { dateToGQLFormat } from '../../helpers/dateHelper';
import { ClockInStatus, useClockService } from '../../services/clockIn/ClockService';
import { WorkTimeDataSource } from '../../services/gql/graphql.generated';
import { useLocationService } from '../../services/location/LocationService';
import { useDeviceLocation } from '../../services/location/useDeviceLocation';
import { SelectInput, SelectInputOption } from '../form/SelectForm';
import { Button } from '../shared/button/Button';
import { DateTimeInput } from '../shared/date/DateTimeInput';
import strings from './clock.translations';

interface clockForgotProps {
  type: ClockInStatus;
  onSuccess: () => void;
}

export const ClockForgot: FC<clockForgotProps> = ({ type, onSuccess }) => {
  const setClockState = useClockService(state => state.setClockState);
  const loading = useClockService(state => state.loadingState);
  const error = useClockService(state => state.errorMessage);
  const clockLocationId = useClockService(state => state.clockLocationId);

  const location = useDeviceLocation(service => service.location);
  const selectedLocationId = useLocationService(state => state.selectedLocation?.id);
  const setSelectedLocation = useLocationService(state => state.setSelectedLocation);
  const locationList = useLocationService(state => state.locationList);

  const locationId = clockLocationId || selectedLocationId;

  const locationOptions = locationList?.map(loc => ({
    id: loc.id.toString(),
    name: loc.name
  }));

  const getDefaultOption = () => {
    return locationOptions.find(opt => opt.id === locationId?.toString());
  };

  const [selectedOption, setSelectedOption] = useState<SelectInputOption | undefined>(getDefaultOption);

  const onLocationChange = (newLocOption?: SelectInputOption) => {
    setSelectedOption(newLocOption);
    if (newLocOption) {
      const newLoc = locationList.find(loc => loc.id.toString() === newLocOption.id);

      if (newLoc) {
        setSelectedLocation(newLoc);
      }
    }
  };

  const handleError = useErrorHandler();

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [isValid, setIsValid] = useState<boolean>(true);

  useEffect(() => {
    if (!selectedDate) return;
    const formattedDate = dateToGQLFormat(selectedDate);
    const cleanedDate = setSeconds(parseJSON(formattedDate ?? new Date()), 0); //Strip seconds from dateTime

    if (cleanedDate > new Date()) {
      setIsValid(false);
    } else {
      setIsValid(true);
    }
  }, [selectedDate]);

  const onForgotSubmit = async () => {
    try {
      if (!selectedDate || locationId === undefined) return;
      await setClockState(type, locationId, WorkTimeDataSource.ManualLate, {
        coords: location.position?.coords,
        manualDate: dateToGQLFormat(selectedDate)
      });
      onSuccess();
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <>
      <p className="text-sm font-content leading-5">{strings.forgotDescription(type)}</p>
      {locationOptions.length > 1 && (
        <SelectInput
          name="selected-location"
          placeholder={strings.selectLocation}
          value={selectedOption}
          onChange={onLocationChange}
          options={locationOptions}
          className="w-full"
          disabled={!!clockLocationId}
        />
      )}
      <label className="text-sm font-content leading-5">{strings.common.date}</label>
      <DateTimeInput name="dateTime" value={selectedDate} onChange={setSelectedDate} />
      {error && <p className="text-sm text-red-500 leading-5">{error}</p>}
      {!isValid && <p className="text-sm text-red-500 leading-5">{strings.errorFutureDate}</p>}
      <Button
        primary
        loading={loading}
        disabled={!isValid}
        onClick={() => {
          onForgotSubmit();
        }}
      >
        {strings.common.submit}
      </Button>
    </>
  );
};
